import React, { useEffect, useState } from 'react';
import { formatBytes, isImage, isVideo } from './uploads';
import './AttachmentList.css';

const URL_PATTERN = /(https?:\/\/[^\s<>"']+)/g;
// Trailing punctuation usually belongs to the sentence, not the link.
const TRAILING = /[.,!?;:)\]]+$/;

// Plain text with http(s) URLs turned into links (Google Docs/Sheets etc.).
export function LinkifiedText({ text }) {
  if (!text) return null;
  const parts = text.split(URL_PATTERN);
  return parts.map((part, i) => {
    if (i % 2 === 0) return <React.Fragment key={i}>{part}</React.Fragment>;
    const trailing = (part.match(TRAILING) || [''])[0];
    const url = trailing ? part.slice(0, -trailing.length) : part;
    return (
      <React.Fragment key={i}>
        <a href={url} target="_blank" rel="noopener noreferrer" className="linkified">{url}</a>
        {trailing}
      </React.Fragment>
    );
  });
}

export function FileIcon({ contentType }) {
  if (/^video\//.test(contentType || '')) {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="23 7 16 12 23 17 23 7"/>
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
      </svg>
    );
  }
  if (/^image\//.test(contentType || '')) {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21 15 16 10 5 21"/>
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  );
}

// Full-size image viewer; closes on backdrop click or Escape.
function Lightbox({ attachment, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="att-lightbox" onClick={onClose}>
      <button type="button" className="att-lightbox-close" onClick={onClose} aria-label="Close">×</button>
      <img src={attachment.url} alt={attachment.name} onClick={(e) => e.stopPropagation()} />
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        className="att-lightbox-open"
        onClick={(e) => e.stopPropagation()}
      >
        Open original
      </a>
    </div>
  );
}

// Sent attachments: images as thumbnails (click for full size), everything
// else as a file card that opens the presigned url. `url` may be null when
// storage is unavailable, in which case the card is shown without a link.
function AttachmentList({ attachments, compact = false }) {
  const [viewing, setViewing] = useState(null);
  if (!attachments || attachments.length === 0) return null;

  const images = attachments.filter((a) => isImage(a) && a.url);
  const files = attachments.filter((a) => !(isImage(a) && a.url));

  return (
    <div className={`att-list ${compact ? 'compact' : ''}`}>
      {images.length > 0 && (
        <div className="att-thumbs">
          {images.map((att) => (
            <button
              type="button"
              key={att.key}
              className="att-thumb"
              onClick={() => setViewing(att)}
              title={att.name}
            >
              <img src={att.url} alt={att.name} loading="lazy" />
            </button>
          ))}
        </div>
      )}
      {files.map((att) => {
        const body = (
          <>
            <span className={`att-icon ${isVideo(att) ? 'video' : ''}`}>
              <FileIcon contentType={att.contentType} />
            </span>
            <span className="att-meta">
              <span className="att-name">{att.name}</span>
              <span className="att-size">
                {formatBytes(att.size)}
                {!att.url && !att.pending && ' · unavailable'}
              </span>
            </span>
          </>
        );
        return att.url ? (
          <a key={att.key} href={att.url} target="_blank" rel="noopener noreferrer" className="att-card">
            {body}
          </a>
        ) : (
          <div key={att.key} className="att-card disabled">{body}</div>
        );
      })}
      {viewing && <Lightbox attachment={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

// Removable chips for files being uploaded from a composer
// (items come from usePendingUploads).
export function PendingAttachments({ uploads }) {
  if (!uploads.items.length && !uploads.notice) return null;
  return (
    <div className="att-pending">
      {uploads.items.map((item) => (
        <div key={item.id} className={`att-chip ${item.status}`} title={item.error || item.name}>
          <span className="att-chip-icon"><FileIcon contentType={item.contentType} /></span>
          <span className="att-chip-text">
            <span className="att-chip-name">{item.name}</span>
            <span className="att-chip-status">
              {item.status === 'uploading' && `Uploading ${Math.round(item.progress * 100)}%`}
              {item.status === 'done' && formatBytes(item.size)}
              {item.status === 'error' && (item.error || 'Upload failed')}
            </span>
          </span>
          {item.status === 'error' && (
            <button type="button" className="att-chip-retry" onClick={() => uploads.retry(item.id)}>
              Retry
            </button>
          )}
          <button
            type="button"
            className="att-chip-remove"
            onClick={() => uploads.remove(item.id)}
            aria-label={`Remove ${item.name}`}
          >
            ×
          </button>
          {item.status === 'uploading' && (
            <span className="att-chip-progress" style={{ width: `${Math.round(item.progress * 100)}%` }} />
          )}
        </div>
      ))}
      {uploads.notice && <p className="att-notice">{uploads.notice}</p>}
    </div>
  );
}

export default AttachmentList;
